import { useState, useRef, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Globe, Check } from 'lucide-react';

const LanguageSelector = ({ variant = 'dropdown', showLabel = true }) => {
  const { currentLanguage, changeLanguage, supportedLanguages, t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Get flag emoji for language
  const getLanguageFlag = (code) => {
    const flags = {
      en: '🇺🇸',
      ar: '🇸🇦',
      es: '🇪🇸',
      fr: '🇫🇷',
      hi: '🇮🇳',
      de: '🇩🇪',
      zh: '🇨🇳',
    };
    return flags[code] || '🌐';
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleLanguageChange = (langCode) => {
    setIsOpen(false);
    changeLanguage(langCode);
  };

  const currentLang = supportedLanguages.find(l => l.code === currentLanguage);

  if (variant === 'compact') {
    return (
      <div className="relative" ref={dropdownRef}>
        <Button 
          variant="ghost" 
          size="icon"
          className="h-9 w-9 rounded-full"
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
          data-testid="language-selector-compact"
        >
          <Globe size={18} />
        </Button>
        
        {isOpen && (
          <div 
            className="absolute top-full mt-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-[9999] py-1"
            style={{ 
              minWidth: '180px',
              right: document.dir === 'rtl' ? 'auto' : '0',
              left: document.dir === 'rtl' ? '0' : 'auto'
            }}
          >
            {supportedLanguages.map((lang) => (
              <button
                key={lang.code}
                onClick={(e) => {
                  e.stopPropagation();
                  handleLanguageChange(lang.code);
                }}
                className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-left"
                data-testid={`lang-option-${lang.code}`}
              >
                <span className="flex items-center gap-2">
                  <span className="text-base">{getLanguageFlag(lang.code)}</span>
                  <span className="text-sm text-slate-700 dark:text-slate-200">{lang.name}</span>
                </span>
                {currentLanguage === lang.code && (
                  <Check size={16} className="text-green-500" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <Button 
        variant="outline" 
        className="gap-2"
        onClick={() => setIsOpen(!isOpen)}
        data-testid="language-selector"
      >
        <Globe size={16} />
        {showLabel && (
          <span className="hidden sm:inline">
            {getLanguageFlag(currentLanguage)} {currentLang?.name}
          </span>
        )}
      </Button>
      
      {isOpen && (
        <div 
          className="absolute top-full mt-2 right-0 w-56 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-[9999] py-1"
        >
          <div className="px-3 py-1.5 text-xs font-medium text-slate-500 uppercase tracking-wider border-b border-slate-200 dark:border-slate-700">
            {t('settings.selectLanguage', 'Select Language')}
          </div>
          {supportedLanguages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-left"
              data-testid={`lang-option-${lang.code}`}
            >
              <span className="flex items-center gap-3">
                <span className="text-lg">{getLanguageFlag(lang.code)}</span>
                <span className="font-medium text-slate-700 dark:text-slate-200">{lang.name}</span>
              </span>
              {currentLanguage === lang.code && (
                <Check size={16} className="text-green-500" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
